import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useQuery } from "@apollo/react-hooks";
import { Icon, Input, Spin } from "antd";
import classnames from "classnames";
import { connect } from "formik";
import gql from "fraql";
import debounce from "lodash/debounce";
import find from "lodash/find";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import omit from "lodash/omit";
import FormFieldFormItem from "./FormFieldFormItem";
import "./FormFieldTeam.scss";

const GET_QUERY_ROOT_FIELDS = gql`
  query FormFieldTeam_GetQueryRootFields {
    __type(name: "query_root") {
      fields {
        name
      }
    }
  }
`;

const SEARCH_TEAMS_FOR_REGISTRATION = gql`
  query FormFieldTeam_SearchTeamsForRegistration($CompetitionId: ID!, $SearchQuery: String!, $AgeGroupId: ID) {
    searchTeamsForRegistration(CompetitionId: $CompetitionId, SearchQuery: $SearchQuery, AgeGroupId: $AgeGroupId) {
      affected_rows
      teams {
        id
        name
        teamManagerName
        isFull
        qualificationScore
        calculatedAgeGroupId
        createdByUserId
      }
    }
  }
`;

const FALLBACK_SEARCH_TEAMS = gql`
  query FormFieldTeam_FallbackSearchTeams($CompetitionId: String!, $NameFilter: String!) {
    Team(
      where: { CompetitionId: { _eq: $CompetitionId }, name: { _ilike: $NameFilter } }
      order_by: { name: asc }
      limit: 50
    ) {
      id
      name
      qualificationScore
      calculatedAgeGroupId
      teamManagerId
      Competition {
        maxTeamMembers
      }
      TeamPeople {
        PersonId
      }
      CreatedBy {
        id
        Person {
          firstName
          lastName
        }
      }
      TeamManager {
        id
        Person {
          firstName
          lastName
        }
      }
      TeamPeople_aggregate {
        aggregate {
          count
        }
      }
    }
  }
`;

const GET_TEAM_BY_ID = gql`
  query FormFieldTeam_GetTeamById($TeamId: String!) {
    Team_by_pk(id: $TeamId) {
      id
      name
      calculatedAgeGroupId
      Competition {
        maxTeamMembers
      }
      TeamPeople_aggregate {
        aggregate {
          count
        }
      }
    }
  }
`;

export const isFull = team => {
  const maxTeamMembers = get(team, "Competition.maxTeamMembers");
  const currentCount = get(team, "TeamPeople_aggregate.aggregate.count", 0);

  if (maxTeamMembers == null) {
    return false;
  }

  return currentCount >= maxTeamMembers;
};

function FormFieldTeam(props) {
  const {
    meta,
    name,
    disabled,
    competitionId,
    teamSelected: teamSelectedFromProp,
    onSelectTeam,
    formik: { values, setFieldValue, setFieldTouched },
  } = props;

  const fieldValue = get(values, name, undefined);
  const containerRef = useRef(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [isUserTyping, setIsUserTyping] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Debounce typed search query by 300ms
  const updateDebouncedQuery = useMemo(
    () =>
      debounce(value => {
        setDebouncedQuery(value);
      }, 300),
    [],
  );

  useEffect(() => {
    return () => {
      updateDebouncedQuery.cancel();
    };
  }, [updateDebouncedQuery]);

  const handleInputChange = useCallback(
    e => {
      const val = e.target.value;
      setSearchQuery(val);
      setIsUserTyping(true);
      setIsDropdownOpen(true);
      updateDebouncedQuery(val);
    },
    [updateDebouncedQuery],
  );

  // Check if searchTeamsForRegistration action exists in schema
  const { data: queryRootFieldsData, error: queryRootFieldsError } = useQuery(GET_QUERY_ROOT_FIELDS);

  const hasSearchTeamsAction = useMemo(() => {
    if (queryRootFieldsError) {
      return false;
    }
    const queryRootFields = get(queryRootFieldsData, "__type.fields", []);
    return !!find(queryRootFields, { name: "searchTeamsForRegistration" });
  }, [queryRootFieldsData, queryRootFieldsError]);

  // Primary search query via Hasura Action
  const shouldExecuteActionSearch =
    hasSearchTeamsAction && debouncedQuery.trim().length >= 3 && !isEmpty(competitionId);
  const { data: actionData, loading: actionLoading } = useQuery(SEARCH_TEAMS_FOR_REGISTRATION, {
    variables: {
      CompetitionId: competitionId,
      SearchQuery: debouncedQuery.trim(),
      AgeGroupId: null,
    },
    skip: !shouldExecuteActionSearch,
    fetchPolicy: "network-only",
  });

  // Fallback search query via direct Team query if action is not registered yet
  const shouldExecuteFallbackSearch =
    !hasSearchTeamsAction && debouncedQuery.trim().length >= 3 && !isEmpty(competitionId);
  const { data: fallbackData, loading: fallbackLoading } = useQuery(FALLBACK_SEARCH_TEAMS, {
    variables: {
      CompetitionId: competitionId,
      NameFilter: `%${debouncedQuery.trim()}%`,
    },
    skip: !shouldExecuteFallbackSearch,
    fetchPolicy: "network-only",
  });

  // Query single team by ID if fieldValue is set and no teamSelected provided by prop
  const shouldQuerySingleTeam = !isEmpty(fieldValue) && isNil(teamSelectedFromProp);
  const { data: singleTeamData } = useQuery(GET_TEAM_BY_ID, {
    variables: { TeamId: fieldValue || "" },
    skip: !shouldQuerySingleTeam,
  });

  const resolvedSelectedTeam = useMemo(() => {
    if (teamSelectedFromProp) {
      return teamSelectedFromProp;
    }
    return get(singleTeamData, "Team_by_pk", null);
  }, [teamSelectedFromProp, singleTeamData]);

  // Formatted search results (all teams in competition matching query regardless of age group)
  const searchResults = useMemo(() => {
    if (debouncedQuery.trim().length < 3) {
      return [];
    }

    if (hasSearchTeamsAction) {
      return get(actionData, "searchTeamsForRegistration.teams", []);
    }

    const rawTeams = get(fallbackData, "Team", []);
    const filteredTeams = rawTeams;

    return filteredTeams.map(t => {
      const managerPerson = get(t, "TeamManager.Person") || get(t, "CreatedBy.Person");
      let managerName = "";
      if (managerPerson) {
        managerName = `${managerPerson.firstName || ""} ${managerPerson.lastName || ""}`.trim();
      }

      return {
        id: t.id,
        name: t.name,
        teamManagerName: managerName || null,
        isFull: isFull(t),
        qualificationScore: t.qualificationScore,
        calculatedAgeGroupId: t.calculatedAgeGroupId,
      };
    });
  }, [debouncedQuery, hasSearchTeamsAction, actionData, fallbackData]);

  const isLoading = actionLoading || fallbackLoading;

  // Handle outside click to close results card
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setIsUserTyping(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelectTeam = useCallback(
    team => {
      setFieldValue(name, team.id);
      setFieldTouched(name, true);
      setSearchQuery(team.name);
      setIsUserTyping(false);
      setIsDropdownOpen(false);
      if (onSelectTeam) {
        onSelectTeam(team);
      }
    },
    [name, setFieldValue, setFieldTouched, onSelectTeam],
  );

  const handleClear = useCallback(
    e => {
      e.stopPropagation();
      setFieldValue(name, undefined);
      setFieldTouched(name, true);
      setSearchQuery("");
      setDebouncedQuery("");
      updateDebouncedQuery.cancel();
      setIsUserTyping(false);
      setIsDropdownOpen(false);
    },
    [name, setFieldValue, setFieldTouched, updateDebouncedQuery],
  );

  const handleInputFocus = useCallback(() => {
    setIsDropdownOpen(true);
  }, []);

  // Compute what text to show in the input
  const displayInputValue = useMemo(() => {
    if (isUserTyping) {
      return searchQuery;
    }
    if (resolvedSelectedTeam && resolvedSelectedTeam.name) {
      return resolvedSelectedTeam.name;
    }
    return searchQuery;
  }, [isUserTyping, searchQuery, resolvedSelectedTeam]);

  // Compute helper text below input
  const helperText = useMemo(() => {
    const trimmed = searchQuery.trim();
    if (trimmed.length > 0 && trimmed.length < 3) {
      return "Type at least 3 letters to search";
    }
    return "Type at least 3 letters of the team name or Team Manager name.";
  }, [searchQuery]);

  const hasSelectedTeam = !isEmpty(fieldValue);
  const showClearButton = !disabled && (hasSelectedTeam || searchQuery.length > 0);

  const renderSuffix = () => {
    if (isLoading) {
      return <Icon type="loading" spin style={{ color: "#1890ff" }} />;
    }
    if (showClearButton) {
      return (
        <Icon type="close-circle" theme="filled" className="form-field-team-search__clear-icon" onClick={handleClear} />
      );
    }
    return <Icon type="search" style={{ color: "#bfbfbf" }} />;
  };

  const label = get(meta, "label", "Find your team");
  const placeholder = get(meta, "placeholder", "Search by team name or Team Manager name");

  return (
    <FormFieldFormItem {...omit(props, ["formik"])} meta={{ ...meta, label }}>
      <div className="form-field-team-search" ref={containerRef}>
        <div className="form-field-team-search__input-container">
          <Input
            placeholder={placeholder}
            value={displayInputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            disabled={disabled}
            suffix={renderSuffix()}
          />
        </div>

        <div className="form-field-team-search__helper-text">{helperText}</div>

        {isDropdownOpen && debouncedQuery.trim().length >= 3 && (
          <div className="form-field-team-search__results-card">
            {isLoading && (
              <div className="form-field-team-search__loading">
                <Spin size="small" />
                <span style={{ marginLeft: 8 }}>Searching teams...</span>
              </div>
            )}

            {!isLoading && searchResults.length > 0 && (
              <>
                <div className="form-field-team-search__results-header">
                  {searchResults.length} team{searchResults.length === 1 ? "" : "s"} found
                </div>
                <div className="form-field-team-search__list">
                  {searchResults.map(team => {
                    const full = !!team.isFull;
                    const isCurrent = team.id === fieldValue;

                    return (
                      <div
                        key={team.id}
                        className={classnames("form-field-team-search__row", {
                          "form-field-team-search__row--disabled": full,
                          "form-field-team-search__row--selected": isCurrent,
                        })}
                        onClick={() => !full && handleSelectTeam(team)}
                      >
                        <div className="form-field-team-search__row-left">
                          <span className="form-field-team-search__team-name">{team.name}</span>
                          {full && <span className="form-field-team-search__full-tag"> [THIS TEAM IS FULL]</span>}
                        </div>
                        <div className="form-field-team-search__row-right">
                          Team Manager: {team.teamManagerName || "-"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {!isLoading && searchResults.length === 0 && (
              <div className="form-field-team-search__no-results">
                No teams found. Check the spelling or create a new team.
              </div>
            )}
          </div>
        )}
      </div>
    </FormFieldFormItem>
  );
}

FormFieldTeam.propTypes = {
  name: PropTypes.string.isRequired,
  competitionId: PropTypes.string.isRequired,
  ageGroupId: PropTypes.string,
  teamSelected: PropTypes.object,
  personId: PropTypes.string,
  meta: PropTypes.shape({
    label: PropTypes.string,
    placeholder: PropTypes.string,
    allowClear: PropTypes.bool,
    required: PropTypes.bool,
    helpText: PropTypes.node,
    displayDefaultLabel: PropTypes.bool,
  }).isRequired,
  disabled: PropTypes.bool.isRequired,
  onSelectTeam: PropTypes.func,
  formik: PropTypes.shape({
    values: PropTypes.object,
    setFieldValue: PropTypes.func.isRequired,
    setFieldTouched: PropTypes.func.isRequired,
  }).isRequired,
};

FormFieldTeam.defaultProps = {
  ageGroupId: null,
  teamSelected: null,
  personId: null,
  onSelectTeam: null,
};

export default connect(FormFieldTeam);
